import React, { useEffect, useRef } from 'react';

interface TimelinePlayheadProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  totalSequenceDuration: number;
  isPlaying: boolean;
  getSequenceTimeFromSourceTime: (srcTime: number) => number;
  onTimeSync?: (seqTime: number) => void;
}

export function TimelinePlayhead({
  videoRef,
  totalSequenceDuration,
  isPlaying,
  getSequenceTimeFromSourceTime,
  onTimeSync,
}: TimelinePlayheadProps) {
  const needleRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);

  // Store callbacks in refs so the RAF loop never needs to restart when they change.
  // This is the key fix: the useEffect dependency array NO LONGER includes onTimeSync.
  const onTimeSyncRef = useRef(onTimeSync);
  useEffect(() => { onTimeSyncRef.current = onTimeSync; }, [onTimeSync]);

  const getSeqTimeRef = useRef(getSequenceTimeFromSourceTime);
  useEffect(() => { getSeqTimeRef.current = getSequenceTimeFromSourceTime; }, [getSequenceTimeFromSourceTime]);

  useEffect(() => {
    if (!isPlaying) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      return;
    }

    const updateNeedle = () => {
      if (videoRef.current && needleRef.current && totalSequenceDuration > 0) {
        const srcTime = videoRef.current.currentTime;
        const seqTime = getSeqTimeRef.current(srcTime);
        const pct = Math.max(0, Math.min(100, (seqTime / totalSequenceDuration) * 100));

        // Direct DOM mutation — no React re-render, no function reference churn
        needleRef.current.style.left = `${pct}%`;

        if (onTimeSyncRef.current) onTimeSyncRef.current(seqTime);
      }
      animRef.current = requestAnimationFrame(updateNeedle);
    };

    animRef.current = requestAnimationFrame(updateNeedle);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
    // INTENTIONALLY only restart RAF when isPlaying or duration changes.
    // onTimeSync and getSequenceTimeFromSourceTime are accessed via refs.
  }, [isPlaying, totalSequenceDuration, videoRef]);

  return (
    <div
      ref={needleRef}
      className="absolute top-0 bottom-0 z-40 pointer-events-none transition-none flex flex-col items-center h-full min-h-[200px]"
      style={{ left: '0%' }}
    >
      <div className="w-3.5 h-3.5 bg-yellow-400 rotate-45 -mt-1 shadow-lg border border-yellow-200 flex-shrink-0" />
      <div className="w-0.5 flex-1 bg-yellow-400 shadow-[0_0_12px_rgba(250,204,21,1)]" />
    </div>
  );
}
